import { Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { ChannelAutoCompleteDataModel } from 'components/MprcAutoComplete/AutoCompleteDataModel';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelAutoComplete } from 'views/knowledge/channels/components/ChannelAutoComplete';


////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    title: string;
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////////

export const SharePublicationDialog: React.FC<IProps> = (props) => {

    const { publicationId, title, onClose } = props;

    const { t } = useTranslation("knowledge");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [text, setText] = useState<string | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<ChannelAutoCompleteDataModel>();

    const canSave = selectedItem != undefined;

    ///
    const handleSave = () => {

        if (!canSave) return;

        setSaving(true);
        api.v1.knowledgePublication.share({
            publicationId,
            channelId: selectedItem!.key,
            comments: text
        })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Feed.Card.Actions.SharePublicationDialog.SavedSuccessMessage"), { variant: "success" });
                    onClose();
                } else {
                    enqueueSnackbar(t("Feed.Card.Actions.SharePublicationDialog.SavedFailMessage"), { variant: "error" });
                }
            })
            .catch(e => {
                const r = MprcResolveRequestErrors(e);
                if (r.fieldsWithError != undefined && (r.fieldsWithError["publicationId"] || r.fieldsWithError["channelId"])) {
                    const errors = (r.fieldsWithError["publicationId"] ?? []).concat(r.fieldsWithError["channelId"] ?? []);
                    errors.forEach(item => enqueueSnackbar(item, { variant: "error" }));
                }
                else {
                    setViewError(e);
                }
            }).finally(() => {
                setSaving(false);
            });
    }

    return (
        <Dialog
            open
            onClose={() => onClose()}
            maxWidth="xs"
            fullWidth
        >
            <MprcDialogTitleClose
                title={t("Feed.Card.Actions.SharePublicationDialog.Title")}
                subtitle={title}
                onClose={() => onClose()}
                shrinkPadding
            />
            <DialogContent>
                <MprcRequestIndicator type="post" visible={saving} />
                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
                <MprcContainer>
                    <ChannelAutoComplete
                        autoFocus
                        selectedItems={selectedItem != undefined ? [selectedItem] : []}
                        onChange={items => items.length > 0 ? setSelectedItem(items[0]) : setSelectedItem(undefined)}
                        multiSelect={false}
                        placeholder={t("Feed.Card.Actions.SharePublicationDialog.ChannelPlaceholder")}
                        label={t("Feed.Card.Actions.SharePublicationDialog.ChannelLabel")}
                        required
                        helperText={t("Feed.Card.Actions.SharePublicationDialog.ChannelHint")}
                    />
                </MprcContainer>
                <TextField
                    value={text ?? ""}
                    placeholder={t("Feed.Card.Actions.SharePublicationDialog.CommentPlaceholder")}
                    multiline={true}
                    rows={10}
                    onChange={(e) => setText(e.target.value)}
                    fullWidth
                    variant="outlined"
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", alignItems: "center" }}>
                <MprcButton
                    text={t("Feed.Card.Actions.SharePublicationDialog.SaveButtonText")}
                    title={t("Feed.Card.Actions.SharePublicationDialog.SaveButtonTooltip")}
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!canSave}
                />
            </DialogActions>
        </Dialog>
    );
}
